import React, {useRef, useState} from 'react';
import {
	Button,
	ButtonDropdownField,
	DropdownList,
	FlexColumn,
	FlexRow,
	Heading,
	Icon,
	IconTooltip,
	Label,
	Modal,
	Text,
	TextField,
} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import Styled from 'styled-components/macro';
import Util from '../shared/util/util';
import CreateClientMutation from '../../mutations/create_client_mutation';
import {getClientInitials} from './clientsAvatarUtil';
import {isoCountries} from '../../constants';

interface Props {
	companyId: string;
	existingClientNames: string[];
	onClose: () => void;
	onClientCreated: (clientId: string, clientName: string) => void;
}

type IsoCountryType = {value: string; label: string};

const AVATAR_SIZE = 150;

const AvatarImg = Styled.img`
	width: ${AVATAR_SIZE}px;
	height: ${AVATAR_SIZE}px;
	object-fit: cover;
	border-radius: 8px;
	flex-shrink: 0;
`;

const AvatarInitials = Styled.div`
	width: ${AVATAR_SIZE}px;
	height: ${AVATAR_SIZE}px;
	border-radius: 8px;
	background-color: var(--sky-sys-color-brand-primary-2, #06f);
	color: #ffffff;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 20px;
	font-weight: 700;
	letter-spacing: 0.3px;
	flex-shrink: 0;
	user-select: none;
`;

const AvatarPlaceholder = Styled.div`
	width: ${AVATAR_SIZE}px;
	height: ${AVATAR_SIZE}px;
	border-radius: 8px;
	background-color: var(--sky-sys-color-background-disabled-1, #f5f3f9);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
`;

const AvatarPlaceholderText = Styled.span`
	font-size: 28px;
	font-weight: 700;
	color: #acacb9;
	user-select: none;
`;

const CreateClientModal = ({companyId, existingClientNames, onClose, onClientCreated}: Props): JSX.Element => {
	const {formatMessage} = useIntl();
	const fileInputRef = useRef<HTMLInputElement>(null);

	const [name, setName] = useState('');
	const [avatarName, setAvatarName] = useState('');
	const [website, setWebsite] = useState('');
	const [street, setStreet] = useState('');
	const [city, setCity] = useState('');
	const [zip, setZip] = useState('');
	const [country, setCountry] = useState<IsoCountryType | null>(null);
	const [vat, setVat] = useState('');
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
	const [isExpanded, setIsExpanded] = useState(false);
	const [duplicateError, setDuplicateError] = useState(false);
	const [fileTypeError, setFileTypeError] = useState(false);
	const [saveError, setSaveError] = useState(false);
	const [saving, setSaving] = useState(false);

	const checkDuplicate = (nameToCheck: string): boolean =>
		existingClientNames.some(n => n.trim().toLowerCase() === nameToCheck.trim().toLowerCase());

	const handleNameBlur = () => {
		setAvatarName(name.trim());
		setDuplicateError(checkDuplicate(name));
	};

	const handleNameChange = (value: string) => {
		setName(value);
		if (duplicateError) setDuplicateError(false);
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		setFileTypeError(false);
		Util.confirmFilePreviewType(file, (valid: boolean) => {
			if (valid) {
				setSelectedFile(file);
				setLogoPreviewUrl(prev => {
					if (prev) URL.revokeObjectURL(prev);
					return URL.createObjectURL(file);
				});
				setFileTypeError(false);
			} else {
				setFileTypeError(true);
			}
		});
	};

	const handleRemoveLogo = () => {
		setSelectedFile(null);
		setLogoPreviewUrl(prev => {
			if (prev) URL.revokeObjectURL(prev);
			return null;
		});
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	const handleSave = () => {
		if (!name.trim()) return;
		if (checkDuplicate(name)) {
			setDuplicateError(true);
			return;
		}
		setSaving(true);
		setSaveError(false);

		const onSuccess = (response: {createClient?: {client?: {node?: {id?: string}}}}) => {
			const clientId = response?.createClient?.client?.node?.id ?? '';
			setSaving(false);
			onClientCreated(clientId, name.trim());
		};

		const onError = () => {
			setSaving(false);
			setSaveError(true);
		};

		const input = {
			companyId,
			name: name.trim(),
			...(website ? {website} : {}),
			...(street ? {street} : {}),
			...(city ? {city} : {}),
			...(zip ? {zip} : {}),
			...(country ? {country: country.value} : {}),
			...(vat ? {vat} : {}),
		};

		const uploadables = selectedFile ? {file: selectedFile} : null;
		Util.CommitMutation(CreateClientMutation, input, onSuccess, true, uploadables, onError);
	};

	const hasLogo = !!logoPreviewUrl;

	const avatarNode = logoPreviewUrl ? (
		<AvatarImg src={logoPreviewUrl} alt="logo preview" />
	) : avatarName ? (
		<AvatarInitials>{getClientInitials(avatarName)}</AvatarInitials>
	) : (
		<AvatarPlaceholder>
			<AvatarPlaceholderText>Logo</AvatarPlaceholderText>
		</AvatarPlaceholder>
	);

	return (
		<Modal
			onCloseModal={() => {
				if (!saving) onClose();
			}}
		>
			<Modal.Title>
				<Heading size={3}>{formatMessage({id: 'clients_page.create_client.title'})}</Heading>
			</Modal.Title>

			<Modal.Content>
				<FlexColumn gap={'l'}>
					<FlexColumn gap={'xs'}>
						<TextField
							label={formatMessage({id: 'clients.client-name'})}
							value={name}
							onValueChange={handleNameChange}
							onBlur={handleNameBlur}
							size="large"
							width="flex"
							hasError={duplicateError}
						/>
						{duplicateError && (
							<Text color={'error'}>
								{formatMessage({id: 'clients_page.create_client.error.duplicate_name'})}
							</Text>
						)}
					</FlexColumn>

					<TextField
						label={formatMessage({id: 'clients.website'})}
						value={website}
						onValueChange={setWebsite}
						size="large"
						width="flex"
					/>

					<FlexRow gap={'s'} alignItems={'center'}>
						<div style={{flex: 1, height: 1, background: 'var(--sky-sys-color-border-decorative, #d3d3df)'}} />
						<Button emphasis="low" onPress={() => setIsExpanded(prev => !prev)}>
							{formatMessage({
								id: isExpanded ? 'clients.view-less' : 'clients.view-more',
							})}
						</Button>
						<div style={{flex: 1, height: 1, background: 'var(--sky-sys-color-border-decorative, #d3d3df)'}} />
					</FlexRow>

					{isExpanded && (
						<FlexColumn gap={'l'}>
							<TextField
								label={formatMessage({id: 'clients.street'})}
								value={street}
								onValueChange={setStreet}
								size="large"
								width="flex"
							/>
							<FlexRow gap={'l'}>
								<TextField
									label={formatMessage({id: 'clients.city'})}
									value={city}
									onValueChange={setCity}
									size="large"
									width="flex"
								/>
								<TextField
									label={formatMessage({id: 'clients.zip'})}
									value={zip}
									onValueChange={setZip}
									size="large"
									width="flex"
								/>
							</FlexRow>

							<FlexColumn gap={'s'}>
								<Label>{formatMessage({id: 'clients.country'})}</Label>
								<DropdownList>
									<DropdownList.Trigger>
										<ButtonDropdownField>
											<Text>
												{country?.label ??
													formatMessage({id: 'clients_page.create_client.field.country_placeholder'})}
											</Text>
										</ButtonDropdownField>
									</DropdownList.Trigger>
									<DropdownList.Content align={'start'}>
										{isoCountries.map((c: IsoCountryType) => (
											<DropdownList.Item key={c.value} onClick={() => setCountry(c)}>
												<FlexRow>
													<Text>{c.label}</Text>
													{c.value === country?.value && <Icon icon={'iconTick'} />}
												</FlexRow>
											</DropdownList.Item>
										))}
									</DropdownList.Content>
								</DropdownList>
							</FlexColumn>

							<FlexColumn gap={'s'}>
								<FlexRow gap={'xs'} alignItems={'center'}>
									<Label>{formatMessage({id: 'clients.tax-code'})}</Label>
									<IconTooltip>
										<IconTooltip.Item title={formatMessage({id: 'clients.tax-code-tooltip'})} />
									</IconTooltip>
								</FlexRow>
								<TextField value={vat} onValueChange={setVat} size="large" width="flex" />
							</FlexColumn>
						</FlexColumn>
					)}

					<FlexRow gap={'l'} alignItems={'center'}>
						{avatarNode}
						<FlexColumn gap={'xs'}>
							<input
								type="file"
								accept=".jpg,.jpeg,.png,.gif"
								style={{display: 'none'}}
								ref={fileInputRef}
								onChange={handleFileChange}
							/>
							<Button
								emphasis="low"
								icon={hasLogo ? 'reload' : 'upload'}
								onPress={() => {
									fileInputRef.current?.click();
								}}
								disabled={saving}
							>
								{formatMessage({id: hasLogo ? 'clients.update-logo' : 'clients.upload-logo'})}
							</Button>
							{hasLogo && (
								<Button emphasis="low" icon="trash" onPress={handleRemoveLogo} disabled={saving}>
									{formatMessage({id: 'clients.remove-logo'})}
								</Button>
							)}
							{fileTypeError && (
								<Text color={'error'}>
									{formatMessage({id: 'clients_page.create_client.error.invalid_file_type'})}
								</Text>
							)}
						</FlexColumn>
					</FlexRow>

					{saveError && (
						<Text color={'error'}>{formatMessage({id: 'clients_page.create_client.error.save_failed'})}</Text>
					)}
				</FlexColumn>
			</Modal.Content>

			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={handleSave} disabled={!name.trim() || saving}>
					{formatMessage({id: 'clients.save-client'})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={onClose} disabled={saving}>
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default CreateClientModal;
